import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import { useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useRetrieveBentleyAccessTokenQuery } from '../../apiClients/identityClient/requests/BentleyAccessTokenApi';
import type { AccessAndIDTokenResponse, ErrorResponse } from '../../apiClients/types';
import {
    BENTLEY_SESSION_EXPIRED,
    LOGIN_ROUTE,
    LOGIN_ERROR_ROUTE,
    LOGIN_ORG_ROUTE,
} from '../../constants';
import { clearLoginError } from '../../store/localStorageHelpers/loginErrorHelper';
import { getCSRFState } from '../../store/sessionStorageHelpers/authStateHelpers';
import { Background } from '../background/Background';
import { LoginContext } from '../loginHandler/LoginContext';

interface RetrieveAccessTokenProps {
    authCode: string;
    forceOauthPrefix: boolean;
    onSuccess?: (data: AccessAndIDTokenResponse) => void;
}

export function RetrieveAccessToken({
    authCode,
    forceOauthPrefix,
    onSuccess,
}: Readonly<RetrieveAccessTokenProps>) {
    const navigate = useNavigate();

    const { data, error } = useRetrieveBentleyAccessTokenQuery({
        authCode,
        forceOauthPrefix,
    });

    useEffect(() => {
        if (data) {
            if (onSuccess) {
                onSuccess(data);
            }
            navigate(LOGIN_ORG_ROUTE);
        }
        if (error) {
            const errorMessage =
                (error as ErrorResponse)?.data?.error || 'unable_to_get_access_token';
            const errorDescription = (error as ErrorResponse)?.data?.error_description || '';
            navigate(
                `${LOGIN_ERROR_ROUTE}?error=${errorMessage}&error_description=${errorDescription}&bentley_token=true`,
            );
        }
    }, [data, error]);

    return (
        <Background>
            <Grid
                item
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100px',
                }}
                alignContent="center"
                justifyContent="center"
                display="grid"
            >
                <CircularProgress />
            </Grid>
        </Background>
    );
}

interface Props {
    /** Optional callback for a successful authorization. The successful token response is provided as an argument. */
    onSuccess?: (data: AccessAndIDTokenResponse) => void;
}

export function BentleyLoginSuccess({ onSuccess }: Readonly<Props>) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { forceOauthPrefix } = useContext(LoginContext);
    const authCode = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const storedState = getCSRFState();
    const serverSide = typeof window === 'undefined';
    // If we're running server side we have no access to the window, so can't do the iframe window operations
    const inIframe = !serverSide && window.self !== window.top;

    clearLoginError();

    useEffect(() => {
        // If state values don't match. Then it is possibly a CSRF attack. so hard bail out of the login flow
        // If we're running in an iFrame we'll check the state in the main app before we get the token.
        if (inIframe) {
            window.parent.postMessage(
                { source: 'bentley-silent-auth', code: authCode, state, error },
                window.parent.origin,
            );
        } else if (state !== storedState) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=invalid_auth_state&bentley_token=true`);
        }
    }, []);

    useEffect(() => {
        if (error) {
            // Going through LOGIN_ERROR_ROUTE will log us out of IMS, which isn't ideal if we can't renew the session BUT we still have a valid access token during silent-auth
            // If we go to the LOGIN_ROUTE we will be routed through to IMS when we click the sign-in button regardless and it can sort the session out from there.
            navigate(`${LOGIN_ROUTE}?error=${BENTLEY_SESSION_EXPIRED}&bentley_token=true`);
        } else if (!authCode) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=no_auth_token&bentley_token=true`);
        }
    }, [authCode, error]);

    if (inIframe) {
        // Don't progress with rendering the app if we're in an iFrame
        return null;
    }

    return (
        <>
            {authCode && state === storedState && (
                <RetrieveAccessToken
                    authCode={authCode}
                    forceOauthPrefix={forceOauthPrefix}
                    onSuccess={onSuccess}
                />
            )}
        </>
    );
}
